import { useEffect, useState } from 'react';
import axios from 'axios';
import { KeyRound, Save, Loader2, CheckCircle2, BarChart3, Search, Globe, Facebook, Linkedin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'ga', label: 'Google Analytics', Icon: BarChart3, placeholder: 'analytics@lawagency360.com' },
  { id: 'gsc', label: 'Google Search Console', Icon: Search, placeholder: 'search@lawagency360.com' },
  { id: 'website', label: 'Website', Icon: Globe, placeholder: 'web@lawagency360.com' },
  { id: 'meta', label: 'Meta (Facebook/Instagram)', Icon: Facebook, placeholder: 'social@lawagency360.com' },
  { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, placeholder: 'social@lawagency360.com' },
];

export default function AccessDefaultsAdmin() {
  const [vals, setVals] = useState({ ga: '', gsc: '', website: '', meta: '', linkedin: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/access-defaults`, { headers: authHeader() })
      .then((res) => setVals({ ...vals, ...(res.data || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await axios.put(`${API}/access-defaults`, vals, { headers: authHeader() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not save defaults.');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="access-defaults-section">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center"><KeyRound className="w-5 h-5 text-violet-600" /></div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Default access emails</h3>
          <p className="text-sm text-slate-500">These pre-fill the "grant access to" field when the team creates a new access request.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="mt-5 space-y-4 max-w-xl">
          {PLATFORMS.map(({ id, label, Icon, placeholder }) => (
            <div key={id} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-slate-500" /></div>
              <label className="w-52 text-sm font-medium text-slate-700 flex-shrink-0">{label}</label>
              <input
                type="email"
                value={vals[id] || ''}
                placeholder={placeholder}
                onChange={(e) => setVals({ ...vals, [id]: e.target.value })}
                className="flex-1 px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                data-testid={`access-default-${id}`}
              />
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="access-defaults-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save defaults
            </button>
            {saved && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium" data-testid="access-defaults-saved"><CheckCircle2 className="w-4 h-4" /> Saved</span>}
          </div>
        </div>
      )}
    </div>
  );
}
